import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import HeaderActions from "../../components/CustomerCM/HeaderActions";
import StatusTabs from "../../components/CustomerCM/Filters/StatusTabs";
import LeadTable, { useLeadTable } from "../../components/CustomerCM/Table/LeadTable";
import { ColumnSettingButton } from "../../components/TableColumnSetting";
import StatsBar from "../../components/CustomerCM/Stats/StatsBar";
import Layout from '../../Layout'
import { ClipboardCheck } from "lucide-react";
import { getAppointments, AppointmentItem } from "../../api/Appointment";
import type { FC } from "react";
import { FilterState, LeadViewMode, toLeadViewMode } from "./Customer";
import AppointmentCalendar from "../OrdersPage/Calendar";
import { useDebounce } from "hooks/useDebounce";
import useAuthStore from "../../store/authStore";
import { APPOINTMENT_PAGE } from "../../utils/appointmentRoute";

const getDateRange = (key: string): { date_from?: string; date_to?: string } => {
    const now = new Date();
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    const startOfDay = (d: Date) => { d.setHours(0, 0, 0, 0); return d; };
    const endOfDay = (d: Date) => { d.setHours(23, 59, 59, 999); return d; };

    if (key === "today") {
        return { date_from: fmt(startOfDay(new Date(now))), date_to: fmt(endOfDay(new Date(now))) };
    }
    if (key === "yesterday") {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        return { date_from: fmt(startOfDay(y)), date_to: fmt(endOfDay(new Date(y))) };
    }
    if (key === "this_week") {
        const mon = new Date(now); mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
        return { date_from: fmt(startOfDay(mon)), date_to: fmt(endOfDay(new Date(now))) };
    }
    if (key === "last_week") {
        const mon = new Date(now); mon.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return { date_from: fmt(startOfDay(mon)), date_to: fmt(endOfDay(sun)) };
    }
    if (key === "this_month") {
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        return { date_from: fmt(startOfDay(first)), date_to: fmt(endOfDay(new Date(now))) };
    }
    if (key === "last_month") {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const last = new Date(now.getFullYear(), now.getMonth(), 0);
        return { date_from: fmt(startOfDay(first)), date_to: fmt(endOfDay(last)) };
    }
    return {};
};

const PipelineBoard = React.lazy(() => import("../../components/CustomerCM/Pipeline/PipelineBoard")) as FC<{
    data: AppointmentItem[];
    loading?: boolean;
    currentPage: number;
    detailBasePath?: string;
    canEdit?: boolean;
    onChanged?: () => void;
}>;

/** Nhớ chế độ xem đã chọn để tải lại trang không nhảy về mặc định. */
const VIEW_STORAGE_KEY = "execution_view_mode";

const readSavedView = (): LeadViewMode => {
    try {
        return toLeadViewMode(localStorage.getItem(VIEW_STORAGE_KEY));
    } catch {
        return "table";
    }
};

const ExecutionPage = () => {
    const { t } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canEdit = isSuperAdmin || hasPermission("execution.edit");
    const [view, setView] = useState<LeadViewMode>(readSavedView);
    const [showStats, setShowStats] = useState(false);
    const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filters, setFilters] = useState<FilterState>({ staffIds: [], statuses: [], timeKey: "" });
    const [searchText, setSearchText] = useState('')
    const debouncedSearch = useDebounce(searchText, 400);
    const leadTable = useLeadTable("execution_schedule_list", "/execution-schedule/detail");
    // Tăng để thanh tab trạng thái đếm lại sau khi đổi trạng thái lịch (kéo thẻ ở Tiến trình)
    const [countRefreshKey, setCountRefreshKey] = useState(0);

    const handleViewChange = (newView: string) => {
        const next = toLeadViewMode(newView);
        setView(next);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch {
            // localStorage bị chặn (chế độ ẩn danh...) -> vẫn đổi view, chỉ không nhớ được
        }
    };

    const fetchData = useCallback(async (page = 1, activeFilters?: FilterState, search?: string) => {
        setLoading(true);
        const f = activeFilters ?? filters;
        const dateRange = getDateRange(f.timeKey);
        try {
            const res = await getAppointments({
                page,
                take: pagination.pageSize,
                type_appointment: 2,
                ...(f.staffIds.length ? { staff_filter: f.staffIds } : {}),
                ...(f.statuses.length ? { statuses: f.statuses } : {}),
                ...dateRange,
                search: search,
            });
            if (res.status) {
                setAppointments(res.data?.items || []);
                setPagination(prev => ({
                    ...prev,
                    current: res.data?.current_page || 1,
                    total: res.data?.total || 0,
                }));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize, filters]);

    // Chế độ Lịch tự tải dữ liệu riêng -> không gọi API danh sách phân trang
    const isCalendar = view === "calendar";

    const handleFiltersChange = useCallback((next: FilterState) => {
        setFilters(next);
        if (!isCalendar) fetchData(1, next);
    }, [fetchData, isCalendar]);

    const handleSearch = (value: string) => {
        setSearchText(value)
    }

    // Có isCalendar: rời chế độ Lịch thì tải lại danh sách theo bộ lọc hiện tại
    useEffect(() => {
        if (isCalendar) return;
        fetchData(1, filters, debouncedSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, debouncedSearch, isCalendar]);

    return (
        <Layout>
            <Helmet>
                <title>{t('page.execution')} | Khothi</title>
            </Helmet>
            <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    {/* LEFT */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#3e91ab] flex items-center justify-center text-white">
                            <ClipboardCheck size={20} />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {t("lead.list_execution")}
                            </h1>
                            <p className="text-[12px] text-gray-500 dark:text-gray-400">
                                {t("lead.description_execution")}
                            </p>
                        </div>
                    </div>
                </div>
                <StatusTabs
                    scope="action"
                    typeAppointment={2}
                    baseParams={{
                        search: debouncedSearch || undefined,
                        ...(filters.staffIds.length ? { staff_filter: filters.staffIds } : {}),
                        ...getDateRange(filters.timeKey),
                    }}
                    value={filters.statuses}
                    onChange={(statuses) => handleFiltersChange({ ...filters, statuses })}
                    refreshKey={countRefreshKey}
                />
                <HeaderActions
                    view={view}
                    setView={handleViewChange}
                    showStats={showStats}
                    setShowStats={setShowStats}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onSearch={handleSearch}
                    statusScope="action"
                    extra={view === "table" && <ColumnSettingButton {...leadTable.settingProps} />}
                />

                {showStats && <StatsBar />}

                {view === "pipeline"
                    ? (
                        <React.Suspense fallback={null}>
                            <PipelineBoard
                                data={appointments}
                                loading={loading}
                                currentPage={APPOINTMENT_PAGE.EXECUTION}
                                detailBasePath="/execution-schedule/detail"
                                canEdit={canEdit}
                                onChanged={() => {
                                    fetchData(pagination.current, filters, debouncedSearch);
                                    setCountRefreshKey((k) => k + 1);
                                }}
                            />
                        </React.Suspense>
                    )
                    : view === "calendar"
                    ? <AppointmentCalendar
                        filters={{
                            type_appointment: 2,
                            search: debouncedSearch || undefined,
                            staff_filter: filters.staffIds,
                            statuses: filters.statuses,
                            ...getDateRange(filters.timeKey),
                        }}
                        detailBasePath="/execution-schedule/detail"
                    />
                    : <LeadTable
                        data={appointments}
                        loading={loading}
                        pagination={pagination}
                        onPageChange={(page) => fetchData(page)}
                        table={leadTable}
                    />
                }
            </div>
        </Layout>
    );
};

export default ExecutionPage;
